import React, { useState, useEffect, useCallback } from 'react';
import { referenceDocumentsApi } from '../../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  Button,
  Input,
  Textarea,
  RadixSelect,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Label,
  Spinner,
} from '@/components/ui';
import { logger } from '../../utils/logger';
import type { ReferenceScopeType } from '../../types/panel-types';
import { SCOPE_TYPE_INFO } from '../../types/panel-types';
import { Check, Upload } from 'lucide-react';

interface ReferenceUploadModalProps {
  onClose: () => void;
  onSuccess: () => void;
  /** Optional project ID - when uploaded from project context, auto-assigns to this project */
  projectId?: string;
  /** Optional project name for display purposes */
  projectName?: string;
  /** Optional customer ID - for Reference Library uploads without project context */
  customerId?: string;
  /** Optional pre-attached file (from drag-and-drop before modal opened) */
  initialFile?: File | null;
  open?: boolean;
}

export default function ReferenceUploadModal({ 
  onClose, 
  onSuccess, 
  projectId, 
  projectName,
  customerId,
  initialFile,
  open = true 
}: ReferenceUploadModalProps) {
  const [step, setStep] = useState<'form' | 'uploading' | 'processing'>('form');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [documentType, setDocumentType] = useState<ReferenceScopeType>('industry-standard');
  const [standardNumber, setStandardNumber] = useState('');
  const [description, setDescription] = useState('');
  const [revisionDate, setRevisionDate] = useState('');
  const [revision, setRevision] = useState('');  // e.g., "Rev A", "2.0", "Draft"
  const [tags, setTags] = useState('');
  const [file, setFile] = useState<File | null>(null);
  
  // Context determines scope automatically:
  // - projectId provided → assignedProjects: [projectId] (project-specific)
  // - no projectId → assignedProjects: [] (All Projects / customer-wide)
  
  // Handle initial file from props (pre-attached from drag-and-drop)
  useEffect(() => {
    if (initialFile) {
      setFile(initialFile);
      // Note: Removed auto-populate from filename - user enters metadata manually
    }
  }, [initialFile]);

  // Supported file types for reference documents
  const SUPPORTED_TYPES: Record<string, string> = {
    'application/pdf': 'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain': 'text/plain',
    'text/markdown': 'text/markdown',
  };

  const SUPPORTED_EXTENSIONS = ['.pdf', '.docx', '.xlsx', '.txt', '.md'];

  const getContentType = (file: File): string => {
    // Check by MIME type first
    if (SUPPORTED_TYPES[file.type]) {
      return SUPPORTED_TYPES[file.type];
    }
    // Fallback to extension-based detection
    const ext = file.name.toLowerCase().split('.').pop();
    switch (ext) {
      case 'pdf': return 'application/pdf';
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'txt': return 'text/plain';
      case 'md': return 'text/markdown';
      default: return 'application/octet-stream';
    }
  };

  /**
   * Validate and set a file
   */
  const validateAndSetFile = useCallback((selected: File) => {
    const ext = '.' + selected.name.toLowerCase().split('.').pop();
    if (!SUPPORTED_EXTENSIONS.includes(ext)) {
      setError('Supported formats: PDF, DOCX, XLSX, TXT, MD');
      return false;
    }
    if (selected.size > 100 * 1024 * 1024) { // 100MB limit
      setError('File size must be less than 100MB');
      return false;
    }
    setFile(selected);
    setError(null);
    // Note: Removed auto-populate from filename - user enters metadata manually
    return true;
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      validateAndSetFile(selected);
    }
  };
  
  // Drop zone handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  }, []);
  
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  }, []);
  
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const firstFile = files[0];
    if (firstFile) {
      validateAndSetFile(firstFile);
    }
  }, [validateAndSetFile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file (PDF, DOCX, XLSX, or TXT)');
      return;
    }

    if (!title) {
      setError('Title is required');
      return;
    }

    if (!standardNumber.trim()) {
      setError('Standard Number is required (e.g., DO-254, ARP4761). This is how the AI will find this document.');
      return;
    }

    try {
      setError(null);
      setStep('uploading');
      setUploadProgress(10);

      // Step 1: Get presigned URL with filename for format detection
      // Scope is determined automatically by context:
      // - From Reference Library (no projectId): All Projects (customer-wide)
      // - From Project References (with projectId): This project only
      const metadata = {
        title: title.trim(),
        documentType,
        description: description.trim(),
        standardNumber: standardNumber.trim(),  // Required - used for AI search and version tracking
        revision: revision.trim() || undefined,  // Version/revision identifier (e.g., "Rev A", "2.0")
        revisionDate: revisionDate || undefined,
        tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        // Context determines scope: empty = All Projects, [projectId] = project-specific
        assignedProjects: projectId ? [projectId] : [],
        // projectId is required by backend for customer isolation
        projectId: projectId || undefined,
        // customerId for Reference Library uploads without project context
        customerId: customerId || undefined,
        // Include filename for multi-format support (DOCX, XLSX, TXT, PDF)
        filename: file.name,
      };

      const uploadData = await referenceDocumentsApi.upload(metadata);
      setUploadProgress(30);

      // Step 2: Upload file to S3 with correct content type
      const contentType = getContentType(file);
      await referenceDocumentsApi.uploadFile(uploadData.uploadUrl, file, contentType);
      setUploadProgress(70);

      // Step 3: Processing will happen automatically via S3 trigger
      setStep('processing');
      setUploadProgress(100);

      // Wait a moment then close
      setTimeout(() => {
        onSuccess();
      }, 2000);

    } catch (err: unknown) {
      logger.error('Upload error:', err);
      setError(err.message || 'Failed to upload document');
      setStep('form');
      setUploadProgress(0);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && step !== 'uploading' && onClose()}>
      <DialogContent 
        className="max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => step === 'uploading' && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Upload Reference Document</DialogTitle>
          {projectName && (
            <p className="text-sm text-muted-foreground">
              For project: <span className="font-medium text-primary">{projectName}</span>
            </p>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-2">
          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div className="space-y-2">
                <Label>
                  Title <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., DO-178C Software Considerations"
                  required
                />
              </div>

              {/* Document Type */}
              <div className="space-y-2">
                <Label>
                  Document Type <span className="text-destructive">*</span>
                </Label>
                <RadixSelect
                  value={documentType}
                  onValueChange={(value) => setDocumentType(value as ReferenceScopeType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(SCOPE_TYPE_INFO) as ReferenceScopeType[]).map((scopeType) => (
                      <SelectItem key={scopeType} value={scopeType}>
                        <span className="flex items-center gap-2">
                          <span>{SCOPE_TYPE_INFO[scopeType].icon}</span>
                          <span>{SCOPE_TYPE_INFO[scopeType].label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </RadixSelect>
              </div>

              {/* Standard Number - Required for AI discoverability */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  Standard/Document Number <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={standardNumber}
                  onChange={(e) => setStandardNumber(e.target.value)}
                  placeholder={
                    documentType === 'industry-standard' ? "e.g., DO-178C, ARP4761A, AS9100D" :
                    documentType === 'advisory-circular' ? "e.g., AC 20-115D, AC 20-152" :
                    documentType === 'guidance' ? "e.g., Order 8110.4C, IP 8110-34" :
                    "e.g., MIL-STD-882E, SAE AS6081"
                  }
                  className={!standardNumber.trim() ? 'border-warning' : ''}
                />
                <p className="text-xs text-muted-foreground">
                  Required for AI search. Documents with the same number are tracked as revisions.
                </p>
              </div>

              {/* Revision */}
              <div className="space-y-2">
                <Label>Revision</Label>
                <Input
                  value={revision}
                  onChange={(e) => setRevision(e.target.value)}
                  placeholder="e.g., Rev A, 2.0, Draft, 2024"
                />
                <p className="text-xs text-muted-foreground">
                  Version identifier. Uploading a new revision of the same document number links them.
                </p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Brief description of this document..."
                />
              </div>

              {/* Revision Date */}
              <div className="space-y-2">
                <Label>Revision Date</Label>
                <Input
                  type="date"
                  value={revisionDate}
                  onChange={(e) => setRevisionDate(e.target.value)}
                />
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <Label>Tags</Label>
                <Input
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="software, certification, safety-critical (comma-separated)"
                />
                <p className="text-xs text-muted-foreground">Comma-separated tags for categorization</p>
              </div>

              {/* Scope Info - Context determines scope automatically */}
              <div className="space-y-2">
                <Label>Available To</Label>
                {projectId && projectName ? (
                  /* Uploading from Project References → project-specific */
                  <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span>📁</span>
                      <span className="font-medium">{projectName}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      This document will be available to this project only.
                    </p>
                  </div>
                ) : (
                  /* Uploading from Reference Library → All Projects (customer-wide) */
                  <div className="p-3 bg-success-light border border-success/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span>🌐</span>
                      <span className="font-medium">All Projects</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      This document will be available to all projects customer-wide.
                    </p>
                  </div>
                )}
              </div>

              {/* File Upload with Drop Zone */}
              <div className="space-y-2">
                <Label>
                  Document File <span className="text-destructive">*</span>
                </Label>
                
                {/* Drop zone */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`
                    border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
                    ${isDraggingOver 
                      ? 'border-primary bg-primary/10' 
                      : file 
                        ? 'border-success bg-success-light' 
                        : 'border-border hover:border-muted-foreground'
                    }
                  `}
                  onClick={() => document.getElementById('file-input')?.click()}
                >
                  {file ? (
                    <div className="flex items-center justify-center gap-3">
                      <Check className="h-5 w-5 text-success" />
                      <div className="text-left">
                        <p className="font-medium text-foreground">{file.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-8 w-8 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground">
                          Drop file here or click to browse
                        </p>
                        <p className="text-sm text-muted-foreground">
                          PDF, DOCX, XLSX, TXT (max 100MB)
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Hidden file input */}
                {/* eslint-disable-next-line no-restricted-syntax -- file inputs require native element */}
                <input
                  id="file-input"
                  type="file"
                  accept=".pdf,.docx,.xlsx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/markdown"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Error */}
              {error && (
                <div className="p-3 bg-destructive-light border border-destructive rounded text-destructive text-sm">
                  {error}
                </div>
              )}

              {/* Actions */}
              <DialogFooter className="pt-4 border-t">
                <Button type="button" variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit">
                  Upload & Process
                </Button>
              </DialogFooter>
            </form>
          )}

          {step === 'uploading' && (
            <div className="text-center py-8">
              <Spinner size="xl" className="mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Uploading Document...</h3>
              <div className="max-w-md mx-auto">
                <div className="w-full bg-muted rounded-full h-2 mb-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    // eslint-disable-next-line no-restricted-syntax -- Dynamic width for progress bar
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-sm text-muted-foreground">{uploadProgress}% complete</p>
              </div>
            </div>
          )}

          {step === 'processing' && (
            <div className="text-center py-8">
              <Check className="mx-auto h-12 w-12 text-success mb-4" />
              <h3 className="text-lg font-medium mb-2">Upload Complete!</h3>
              <p className="text-sm text-muted-foreground">
                Document is being processed in the background.
                <br />
                Processing time varies by format and size (typically 1-5 minutes).
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
